import { GridOptions } from 'ag-grid-community';
import { Component, Inject, Input, LOCALE_ID, OnInit, ViewChild } from '@angular/core';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerDateRangeInfo } from 'src/shared/models/module-manager/module-manager-settings.models';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { LoginLogout } from './login-logout.models';
import { formatDate } from '@angular/common';
import { ModuleManagerExportFormat, ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { IModuleManagerMenuItem, ModuleManagerCheckMenu, ModuleManagerMenuItemType } from 'src/shared/models/module-manager/module-manager-menu-item.model';
import { LoginLogoutService } from './login-logout.service';
import { AgGridAngular } from 'ag-grid-angular';

@Component({
    selector: 'cv-login-logout',
    templateUrl: './login-logout.component.html',
    styleUrls: ['./login-logout.component.scss']
})
export class LoginLogoutComponent implements OnInit {

    @ViewChild('loginLogoutWidgetGrid') agGrid: AgGridAngular;
    @Input() public moduleManager: ModuleManager;
    @Input() private moduleKey: string;

    private gridState: any = null;
    private isDataLoading: boolean = false;
    private tableDisplayItems = [];

    public data: LoginLogout[] = null; 
    public filterValue = ""; 
    public showFilter = false;

    // Ag-grid configuration
    public gridOptions: GridOptions = null;
    public isDataFirstLoaded: boolean = false;
    public hideTotal = false;

    private _saveStateTimeoutHandle: NodeJS.Timeout = null;
    private stateSaveTimer = null;
    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting; }
    
    constructor(
        private service: LoginLogoutService,
        private cvLocalization: CVLocalizationService,
        private AgGridHelperService: AgGridHelperService,
        @Inject(LOCALE_ID) private locale: string,
    ) { }

    ngOnInit() {
        this.moduleManager.showLoader = true;

        this.tableDisplayItems = [ // items for the display menu that uses both radio buttons and checkboxes
            {
                displayName: this.cvLocalization.getCachedText("Module_Menu", "Show Totals", "Show Totals"),
                inputType: 'radio',
                value: false,
                checked: this.hideTotal
            },
            {
                displayName: this.cvLocalization.getCachedText("Common", "None", "None"),
                inputType: 'radio',
                value: true,
                checked: this.hideTotal
            },
        ];

        this.configureModuleManager();
        this.setupGrid();
    }

    /**
     * Set various options and settings on the module manager object
     */
    private configureModuleManager() {
        this.moduleManagerSetOptions();
        this.moduleManagerAddMenuItems();
        this.moduleManagerAddHeaderItems();
        this.moduleManagerAddStateAndSettingsItems();
    }

    /**
     * Set options, such as refresh handling, on the module manager object
     */
    private async moduleManagerSetOptions() {
        await this.loadLocalization();

        this.moduleManager.setOptions({
            enableWidthClass: false,
            widgetMode: false,
            onSettingsApplied: settings => {
                this.loadData(false); // for example, call your data loading function once settings have been loaded
            },
            hasRefresh: true,
            onExport: this.onExport.bind(this),
            refreshType: ModuleManagerRefreshType.FiveMinute,
            onRefresh: isAutoRefresh => {
                if(!isAutoRefresh)
                    this.moduleManager.showLoader = true;
                this.loadData(isAutoRefresh);
            },
            canRefresh: () => !this.isDataLoading,
        });
    }

    /**
     * Add menu items for the module onto the module manager object
     */
    private moduleManagerAddMenuItems() {
        // See Module Container's addMenuItem() for more on menu items
        this.moduleManager.addMenuItem({
            name: "Columns",
            icon: "columns",
            type: ModuleManagerMenuItemType.CheckMenu,
            
            getEnabled: () => {
                return this.agGrid?.api && this.gridOptions.columnDefs.length > 0;
            },
            getCheckItems: () => {
                return this.gridState?.columns;
            },
            getItemName: (i) => {
                var colState = this.gridState.columns[i];
                var column = this.agGrid?.api.getColumn(colState.name);

                if (column) return column.getColDef().headerName;
                return '';
            },
            isItemChecked: (i) => {
                return this.gridState.columns[i].visible;
            },
            onCheckClicked: (i) => {
                this.toggleColumnVisible(i);
            }
        } as ModuleManagerCheckMenu);


        this.moduleManager.addMenuItem({
            name: "Table Display", // Menu items are localized automatically by Module Container.  Keys under section Module_Menu
            icon: "tableDisplay",
            getEnabled: () => true,
            type: ModuleManagerMenuItemType.RadioCheckMenu,
            getCheckItems: () => this.tableDisplayItems,
            getItemName: i => this.tableDisplayItems[i].displayName,
            getItemInputType: i => { return i <= this.tableDisplayItems.length - 1 ? this.tableDisplayItems[i]?.inputType : 'checkbox' },
            isItemChecked: i => this.tableDisplayItems[i].checked,
            onCheckClicked: (i) => {
                if (this.tableDisplayItems[i].inputType === 'radio') {
                    this.hideTotal = this.tableDisplayItems[i].value;
                    this.tableDisplayItems.filter(i => i.inputType === 'radio').forEach((item) => { item.checked = this.hideTotal });
                    this.toggleTotalsRow();
                }
                else {
                    this.tableDisplayItems[i].checked = !this.tableDisplayItems[i].checked;
                }
            },
            readOnlyEnabled: true
        } as IModuleManagerMenuItem);
    }

    /**
     * Set header items on the module manager object
     */
    private moduleManagerAddHeaderItems() {
        // Example header item.  Usually used for the magnifying glass for the search bar.
        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });
    }

    /** 
     * Set state items and settings items on the module manager object 
     */
    private moduleManagerAddStateAndSettingsItems() {
        // States - used for saving values that are not set in settings
        //  example: what column is sorted on a table
        // To save a state: call this.moduleManager.saveState() and all state values will be saved
        this.moduleManager.addState({
            name: "gridState",
            getStateData: () => {
                return this.gridState;
            },
            onStateLoaded: stateData => {
                this.gridState = stateData;
                if (this.agGrid?.api)
                    this.AgGridHelperService.loadGridState(this.agGrid?.api, stateData);
            },
            autoLoad: true
        });

        this.moduleManager.addState({
            name: "TotalsRow",
            getStateData: () => this.hideTotal,
            onStateLoaded: (state: boolean) => {
                this.hideTotal = state;
                if(this.data)
                    this.updateTotal();
                this.tableDisplayItems.filter(i => i.inputType === 'radio').forEach((item) => { item.checked = !!state });
            },
            autoLoad: true,
        });
    }

    /**
     * Cache the section(s) that is/are needed for localization
     */
    private async loadLocalization() {
        await this.cvLocalization.GetSection("Login_Logout_Module");
        await this.cvLocalization.GetSection("Module_Menu");
    }

    /** 
     * Get login/logout data and update ag-Grid 
     */
    private async loadData(isAutoRefresh) {
        if (this.isDataLoading)
            return; // don't have multiple outgoing requests
        this.isDataLoading = true;

        if (!isAutoRefresh)
            this.moduleManager.showLoader = true;

        try {
            this.data = await this.service.getLoginLogout(this.moduleKey, isAutoRefresh);

            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data);
                this.agGrid?.api.redrawRows();
                this.updateTotal();
            }
            // else, the grid isn't ready, but the onGridReady() event handler will load up its initial values.

            this.moduleManager.showLoader = false;
            this.moduleManager.setAutoRefreshReady();
        } catch (err) {
            this.moduleManager.handleLoadError(err, isAutoRefresh);
        } finally {
            this.isDataLoading = false;
            this.isDataFirstLoaded = true;
            this.moduleManager.container.showDateRange = true;
        }
    }

    private getLocalizationString(key: string, defaultValue: string): string {
        return this.cvLocalization.getCachedText("Login_Logout_Module", key, defaultValue);
    }

    private setupGrid() {
        this.gridOptions = this.AgGridHelperService.createGridOptions({
            defaultColDef: {
                minWidth: this.AgGridHelperService.defaultMinWidth,
            },
            columnDefs: [
                {
                    field: "Group",
                    colId: "Group",
                    headerName: this.getLocalizationString("Group", "Group"),
                    minWidth: 200,
                    filter: 'agTextColumnFilter',
                    type: "groupColumn",
                    valueFormatter: (params) => {
                        if (params.value == "Totals") {
                            return this.getLocalizationString("Totals", "Totals");
                        }
                        if (params.value == "Averages") {
                            return this.getLocalizationString("Averages", "Averages");
                        }
                        return params.data.Group.groupName;
                    }
                },
                {
                    field: "FirstLogin",
                    colId: "FirstLogin",
                    headerName: this.getLocalizationString("First_Login", "First Login"),
                    type: 'numericColumn',
                    minWidth: 140,
                    comparator: this.compareDate,
                    valueFormatter: (params) => {
                        if (params.value == 'footer') return '';
                        if (!params.value) return 'N/A';

                        let format = CVAngularDateTimeFormat;
                        
                        let dateRange = ModuleManagerDateRangeInfo.fromObject(this.moduleManager.moduleSettings.selectedDateRange);
                        if (dateRange.displayName.toLowerCase() == 'today' || dateRange.endDate?.compareTo(dateRange.startDate) == 0) 
                            format = CVAngularTimeFormat;
                            
                        return formatDate(new Date(params.value), format, this.locale);
                    }
                },
                {
                    field: "LastLogin",
                    colId: "LastLogin",
                    headerName: this.getLocalizationString("Last_Login", "Last Login"),
                    type: 'numericColumn',
                    minWidth: 140,
                    comparator: this.compareDate,
                    valueFormatter: (params) => {
                        if (params.value == 'footer') return '';
                        if (!params.value) return 'N/A';

                        let format = CVAngularDateTimeFormat;
                        
                        let dateRange = ModuleManagerDateRangeInfo.fromObject(this.moduleManager.moduleSettings.selectedDateRange);
                        if (dateRange.displayName.toLowerCase() == 'today' || dateRange.endDate?.compareTo(dateRange.startDate) == 0) 
                            format = CVAngularTimeFormat;
                            
                        return formatDate(new Date(params.value), format, this.locale);
                    }
                },
                {
                    field: "LastLogout",
                    colId: "LastLogout",
                    headerName: this.getLocalizationString("Last_Logout", "Last Logout"),
                    type: 'numericColumn',
                    minWidth: 150,
                    comparator: this.compareDate,
                    valueFormatter: (params) => {
                        if (params.value == 'footer') return '';
                        if (!params.value) return 'N/A';

                        let format = CVAngularDateTimeFormat;
                        
                        let dateRange = ModuleManagerDateRangeInfo.fromObject(this.moduleManager.moduleSettings.selectedDateRange);
                        if (dateRange.displayName.toLowerCase() == 'today' || dateRange.endDate?.compareTo(dateRange.startDate) == 0) 
                            format = CVAngularTimeFormat;
                            
                        return formatDate(new Date(params.value), format, this.locale);
                    }
                },
                {
                    field: "Logins",
                    colId: "Logins",
                    headerName: this.getLocalizationString("Logins", "Logins"),
                    type: 'numericColumn',
                    minWidth: 90,
                },
                {
                    field: "Logouts",
                    colId: "Logouts",
                    headerName: this.getLocalizationString("Logouts", "Logouts"),
                    type: 'numericColumn',
                    minWidth: 90,
                },
                {
                    field: "TimeLoggedIn",
                    colId: "TimeLoggedIn",
                    headerName: this.getLocalizationString("Time_Logged_In", "Time Logged In"),
                    type: 'numericColumn',
                    minWidth: 140,
                    valueFormatter: (params) => {
                        var hours = Math.floor(params.value / 3600);
                        var minutes = Math.floor((params.value - (hours * 3600)) / 60);
                        var seconds = params.value - (hours * 3600) - (minutes * 60);
                        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                    }
                },
                {
                    field: "TimeLoggedOut",
                    colId: "TimeLoggedOut",
                    headerName: this.getLocalizationString("Time_Logged_Out", "Time Logged Out"),
                    type: 'numericColumn',
                    minWidth: 140,
                    valueFormatter: (params) => {
                        var hours = Math.floor(params.value / 3600);
                        var minutes = Math.floor((params.value - (hours * 3600)) / 60);
                        var seconds = params.value - (hours * 3600) - (minutes * 60);
                        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
                    }
                }
            ],
            context: { moduleManager: this.moduleManager },
            onGridReady: params => {
                if (this.data) {
                    params.api.setGridOption('rowData', this.data);
                    params.api.sizeColumnsToFit();
                    this.updateTotal();
                }
                if (this.gridState)
                    this.AgGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
                this.applyFilter();
            },

            // This function defines which field from the row data is the "primary key".  It's used for delta updates between different setRowData() calls.
            // This "ID" property is the same as the above columnDef.  Usually it'll be a group id.  If there is no "primary key" for the data you're trying to display.
            getRowId: ({ data: rowData }) => rowData.ID,

            isExternalFilterPresent: () => this.filterValue.length > 0,
            doesExternalFilterPass: node => this.AgGridHelperService.doesExternalFilterPass(node, this.filterValue), 
            
            onSortChanged: () => this.updateTableState(),
            onColumnMoved: () => this.updateTableState(),
            onColumnPinned: () => this.updateTableState(),
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.queueStateSave();
                    this.agGrid?.api.sizeColumnsToFit();
                }
            },
            onColumnVisible: event => {
                this.updateTotal();
                this.updateTableState();
            }
        }, {
            enableCopy: true
        });

        if (this.agGrid?.api)
            this.AgGridHelperService.loadGridState(this.agGrid?.api, this.gridState);
    }
    
    /** 
     *  Called by AngularJS when the search bar's value has changed.
     */
    public applyFilter() {
        if (this.agGrid?.api) { // notify ag-grid that the filter changed
            this.agGrid?.api.onFilterChanged(); // Used with the External Filter.
        }
    };

    /**
     * Called when the clear button on the filter box is clicked.  Clears the filter
     */
     public clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    };

    /** 
     * Compare dateA and dateB and returns the result 
     */
     private compareDate(dateA, dateB, nodeA, nodeB, reverse: boolean): number {
        return new Date(dateA).compareTo(new Date(dateB));
    }

    /**
     * Get formated data ready to export
     * @param format
     */
     public async onExport(format: ModuleManagerExportFormat) {
        this.moduleManager.showLoader = true;
        let sort = {
            order: []
        };
        let agState = this.agGrid?.api.getColumnState();
        for (const state of agState) {
            sort.order[state.sortIndex] = [Number(state.colId), state.sort];
        }
        const response = await this.service.getDataForExport(this.moduleKey, format, JSON.stringify(sort));
        this.moduleManager.showLoader = false;
        return response;
    }

    /**
     * Saves the grid's state save, debounced to 500ms.
     */
     private queueStateSave() {
        if (this.stateSaveTimer !== false) {
            clearTimeout(this.stateSaveTimer);
        }
         // Create a timer to save the state 500ms from now.
         this.stateSaveTimer = window.setTimeout(() => {
             this.stateSaveTimer = null;
             if (this.agGrid?.api)
                 this.gridState = this.AgGridHelperService.getGridState(this.agGrid?.api);
             this.moduleManager.saveState(); // This will save all moduleManager.addState definitions
         }, 500/*ms*/);
    }

    /** 
     * Set visibility of a column when the user toggles a column in columns menu 
     */
     private toggleColumnVisible(i: number): boolean{
        var column = this.gridState.columns[i];
        var isHidden = !column.visible;

        this.agGrid?.api.setColumnVisible(column.name, isHidden);

        this.updateTableState();
        return isHidden;
    }

    /**
     * Shows and hides the search/filter bar
     */
     private toggleOpenSearch() { // called by moduleManager.addHeaderItem for search bar.
        if (this.showFilter) {
            this.showFilter = false;
            this.clearFilter();
        } else {
            this.showFilter = true;
        }
    };

    /** 
     * Save state for ag-Grid 
     */
     private updateTableState(): void{
        if (!this.agGrid?.api) return;

        var gridState = this.AgGridHelperService.getGridState(this.agGrid?.api);
        if (gridState != null){
            this.gridState = gridState;
        }
        this.queueStateSave();
    }

    /** 
     * Calculate and display aggragation as pinned bottom row in ag-Grid 
     */
    public updateTotal(): void{
        if (!this.agGrid?.api) return;

        if (this.hideTotal) {
            this.agGrid?.api.setGridOption('pinnedBottomRowData', []);
            return;
        }

        var loginsSum = 0;
        var logoutsSum = 0;
        var TimeLoggedInSum = 0;
        var TimeLoggedOutSum = 0;

        //getting totals
        for (var d of this.data) {
            loginsSum += d.Logins;
            logoutsSum += d.Logouts;
            TimeLoggedInSum += d.TimeLoggedIn;
            TimeLoggedOutSum += d.TimeLoggedOut;
        }

        //getting avgs - will be implemented in the future
        //if (this.hideTotal) {
        //    loginsSum = parseFloat((loginsSum / this.data.length).toFixed(2));
        //    logoutsSum = parseFloat((logoutsSum / this.data.length).toFixed(2));
        //    TimeLoggedInSum = Math.round(TimeLoggedInSum / this.data.length);
        //    TimeLoggedOutSum = Math.round(TimeLoggedOutSum / this.data.length);
        //}

        this.agGrid?.api.setGridOption('pinnedBottomRowData', [{
            Group: !this.hideTotal ? "Totals" : "Averages",
            FirstLogin: 'footer',
            LastLogin: 'footer',
            LastLogout: 'footer',
            Logins: loginsSum,
            Logouts: logoutsSum,
            TimeLoggedIn: TimeLoggedInSum,
            TimeLoggedOut: TimeLoggedOutSum
        }]);
    }

    /**
    * Toggles between showing and hiding the totals row
    * Callback on the menu item
    */
    toggleTotalsRow() {
        this.updateTotal();
        this.queueStateSave();
        this.moduleManager.requestPublishNeeded();
    }
}
